import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragEndEvent,
  DragStartEvent,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  getFirstCollision,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GameListItem } from '../../services/contents_api';
import {
  TIER_KEYS,
  TIER_STYLES,
  UNRANKED_KEY,
  TierKey,
  isTierKey,
} from './tierConfig';

type ContainerKey = TierKey | typeof UNRANKED_KEY;

interface TierListEditorProps {
  items: GameListItem[];
  setItems: React.Dispatch<React.SetStateAction<GameListItem[]>>;
  onRemove: (gameId: number) => void;
}

const CONTAINER_IDS: ContainerKey[] = [...TIER_KEYS, UNRANKED_KEY];

const TIER_ORDER: Record<ContainerKey, number> = {
  S: 0,
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  F: 5,
  [UNRANKED_KEY]: 6,
};

const containerOf = (item: GameListItem): ContainerKey =>
  isTierKey(item.tier) ? item.tier : UNRANKED_KEY;

const findContainerById = (
  id: number | string,
  items: GameListItem[]
): ContainerKey | null => {
  if (typeof id === 'string') {
    if (isTierKey(id)) return id;
    if (id === UNRANKED_KEY) return UNRANKED_KEY;
    return null;
  }
  const item = items.find((i) => i.id === id);
  return item ? containerOf(item) : null;
};

const DragPreview: React.FC<{ item: GameListItem }> = ({ item }) => (
  <Box
    sx={{
      width: { xs: 52, sm: 60, md: 68 },
      aspectRatio: '3/4',
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: 'background.paper',
      boxShadow:
        '0 12px 28px rgba(0, 0, 0, 0.55), 0 0 0 2px rgba(217, 255, 0, 0.65)',
      transform: 'scale(1.08) rotate(-1.5deg)',
      pointerEvents: 'none',
    }}
  >
    <Box
      component="img"
      src={item.game_cover || '/image/no_image.png'}
      alt={item.game_name}
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  </Box>
);

const TierRow: React.FC<{
  tier: TierKey;
  items: GameListItem[];
  onRemove: (gameId: number) => void;
  isLast: boolean;
}> = ({ tier, items, onRemove, isLast }) => {
  const { setNodeRef, isOver, active } = useDroppable({ id: tier });
  const style = TIER_STYLES[tier];

  // 自分のコンテナからのドラッグ中はハイライト不要
  const showHighlight =
    isOver &&
    active != null &&
    (() => {
      const activeData = active.data.current as { container?: ContainerKey } | undefined;
      return activeData?.container !== tier;
    })();

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: { xs: 88, sm: 100, md: 112 },
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {/* ラベル列: ガラスチップ風のプレート */}
      <Box
        sx={{
          width: { xs: 56, sm: 64, md: 72 },
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${style.accent}10 0%, transparent 100%)`,
        }}
      >
        <Box
          sx={{
            width: { xs: 34, sm: 40, md: 44 },
            height: { xs: 34, sm: 40, md: 44 },
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${style.accent}38 0%, ${style.accent}18 100%)`,
            border: `1px solid ${style.accent}5e`,
            boxShadow: showHighlight
              ? `0 0 14px ${style.accent}55, inset 0 1px 0 ${style.accent}55`
              : `0 1px 6px ${style.accent}22, inset 0 1px 0 ${style.accent}33`,
            transition: 'box-shadow 0.15s ease',
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Inter", "SF Pro Display", "Roboto", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
              color: style.accent,
              letterSpacing: '0.04em',
              lineHeight: 1,
              textShadow: `0 0 10px ${style.accent}77`,
            }}
          >
            {style.label}
          </Typography>
        </Box>
      </Box>
      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          minWidth: 0,
          p: { xs: 0.75, sm: 1 },
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 0.6, sm: 0.85 },
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          background: showHighlight
            ? `linear-gradient(90deg, ${style.accent}26 0%, ${style.accent}14 40%, ${style.accent}08 100%)`
            : `linear-gradient(90deg, ${style.accent}12 0%, ${style.accent}06 30%, transparent 70%)`,
          transition: 'background 0.15s ease',
        }}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={rectSortingStrategy}
        >
          {items.map((item) => (
            <TierItemWithContainer
              key={item.id}
              item={item}
              container={tier}
              onRemove={onRemove}
            />
          ))}
        </SortableContext>
      </Box>
    </Box>
  );
};

// container 情報を data に持たせた版
const TierItemWithContainer: React.FC<{
  item: GameListItem;
  container: ContainerKey;
  onRemove: (gameId: number) => void;
}> = ({ item, container, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, data: { container } });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        width: { xs: 52, sm: 60, md: 68 },
        flexShrink: 0,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          aspectRatio: '3/4',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          cursor: 'grab',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          '&:active': { cursor: 'grabbing' },
          touchAction: 'manipulation',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      >
        <Box
          component="img"
          src={item.game_cover || '/image/no_image.png'}
          alt={item.game_name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
        />
      </Box>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.game_id);
        }}
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          bgcolor: 'rgba(0, 0, 0, 0.55)',
          '&:hover': { bgcolor: 'error.main', color: 'white' },
          padding: '2px',
        }}
      >
        <ClearIcon sx={{ fontSize: '0.75rem' }} />
      </IconButton>
    </Box>
  );
};

const UnrankedPool: React.FC<{
  items: GameListItem[];
  onRemove: (gameId: number) => void;
}> = ({ items, onRemove }) => {
  const { setNodeRef, isOver, active } = useDroppable({ id: UNRANKED_KEY });

  const showHighlight =
    isOver &&
    active != null &&
    (() => {
      const activeData = active.data.current as { container?: ContainerKey } | undefined;
      return activeData?.container !== UNRANKED_KEY;
    })();

  return (
    <Box sx={{ mt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, px: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.62rem',
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            fontFamily: '"Inter", "SF Pro Display", "Roboto", sans-serif',
          }}
        >
          Unranked
        </Typography>
        {items.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '0.62rem',
              fontWeight: 500,
            }}
          >
            {items.length}
          </Typography>
        )}
      </Box>
      <Box
        ref={setNodeRef}
        sx={{
          minHeight: 92,
          p: 1.25,
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 0.6, sm: 0.85 },
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          backgroundColor: showHighlight
            ? 'rgba(255, 255, 255, 0.045)'
            : 'rgba(255, 255, 255, 0.015)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: 2,
          transition: 'background-color 0.12s ease',
        }}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={rectSortingStrategy}
        >
          {items.length === 0 ? (
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.25)',
                fontSize: '0.7rem',
                m: 'auto',
                fontStyle: 'italic',
              }}
            >
              ここにドラッグで未分類へ
            </Typography>
          ) : (
            items.map((item) => (
              <TierItemWithContainer
                key={item.id}
                item={item}
                container={UNRANKED_KEY}
                onRemove={onRemove}
              />
            ))
          )}
        </SortableContext>
      </Box>
    </Box>
  );
};

const TierListEditor: React.FC<TierListEditorProps> = ({
  items,
  setItems,
  onRemove,
}) => {
  const [activeId, setActiveId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const grouped = useMemo(() => {
    const groups: Record<ContainerKey, GameListItem[]> = {
      S: [],
      A: [],
      B: [],
      C: [],
      D: [],
      F: [],
      [UNRANKED_KEY]: [],
    };
    items.forEach((item) => {
      groups[containerOf(item)].push(item);
    });
    return groups;
  }, [items]);

  const activeItem = useMemo(
    () => (activeId != null ? items.find((i) => i.id === activeId) ?? null : null),
    [activeId, items]
  );

  // コンテナ優先のカスタム衝突検出:
  //  1. ポインタが直接あたっているコンテナを優先
  //  2. なければ rectIntersection でコンテナを検出
  //  3. それでも見つからなければ items 間の closestCenter で代用
  const collisionDetection: CollisionDetection = (args) => {
    // ポインタが直接コンテナに当たっているか確認
    const pointerCollisions = pointerWithin(args);
    const pointerContainer = pointerCollisions.find((c) =>
      CONTAINER_IDS.includes(c.id as ContainerKey)
    );
    if (pointerContainer) return [pointerContainer];

    // 矩形交差でコンテナを探す
    const rectCollisions = rectIntersection(args);
    const rectContainer = rectCollisions.find((c) =>
      CONTAINER_IDS.includes(c.id as ContainerKey)
    );
    if (rectContainer) return [rectContainer];

    // 最後に items に対する closestCenter（同コンテナ内並び替え用）
    const itemCollisions = closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter(
        (c) => !CONTAINER_IDS.includes(c.id as ContainerKey)
      ),
    });
    const first = getFirstCollision(itemCollisions);
    return first ? itemCollisions : pointerCollisions;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    setItems((prev) => {
      const activeContainer = findContainerById(active.id as number, prev);
      const overContainer = findContainerById(over.id as number | string, prev);
      if (!activeContainer || !overContainer) return prev;

      // 同コンテナ内: arrayMove で並び替え
      if (activeContainer === overContainer) {
        if (active.id === over.id) return prev;
        const oldIndex = prev.findIndex((i) => i.id === active.id);
        const newIndex = prev.findIndex((i) => i.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      }

      // 別コンテナ: tier 更新 + flat 配列の適切な位置に挿入
      const activeIndex = prev.findIndex((i) => i.id === active.id);
      if (activeIndex === -1) return prev;
      const activeItemSrc = prev[activeIndex];
      const newTier: string | null =
        overContainer === UNRANKED_KEY ? null : overContainer;

      const without = [
        ...prev.slice(0, activeIndex),
        ...prev.slice(activeIndex + 1),
      ];

      let insertIndex: number;
      if (CONTAINER_IDS.includes(over.id as ContainerKey)) {
        // コンテナ自体にドロップ → その tier の末尾
        let lastIdx = -1;
        without.forEach((it, idx) => {
          if (containerOf(it) === overContainer) lastIdx = idx;
        });
        if (lastIdx !== -1) {
          insertIndex = lastIdx + 1;
        } else {
          // 空のコンテナ → tier 順を維持して挿入
          const targetOrder = TIER_ORDER[overContainer];
          const idx = without.findIndex(
            (it) => TIER_ORDER[containerOf(it)] > targetOrder
          );
          insertIndex = idx === -1 ? without.length : idx;
        }
      } else {
        // 別 tier の item にドロップ → その item の位置に挿入
        const overIdx = without.findIndex((i) => i.id === over.id);
        insertIndex = overIdx >= 0 ? overIdx : without.length;
      }

      return [
        ...without.slice(0, insertIndex),
        { ...activeItemSrc, tier: newTier },
        ...without.slice(insertIndex),
      ];
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Box
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.025)',
        }}
      >
        {TIER_KEYS.map((tier, idx) => (
          <TierRow
            key={tier}
            tier={tier}
            items={grouped[tier]}
            onRemove={onRemove}
            isLast={idx === TIER_KEYS.length - 1}
          />
        ))}
      </Box>
      <UnrankedPool items={grouped[UNRANKED_KEY]} onRemove={onRemove} />
      <DragOverlay dropAnimation={{ duration: 180 }}>
        {activeItem ? <DragPreview item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default TierListEditor;
